import {
  createContext,
  useRef,
  useEffect,
  ReactNode,
  RefObject,
  useContext,
} from "react"

type ScrollContextType = {
  activeMoveRef: React.RefObject<HTMLButtonElement>
}

export const ScrollContext = createContext<ScrollContextType>(
  {} as ScrollContextType
)

const ScrollProvider = ({
  children,
  dependency,
}: {
  children: (ref: RefObject<HTMLDivElement>) => ReactNode
  dependency: any
}) => {
  const activeMoveRef = useRef<HTMLButtonElement>(null)
  const movesContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const activeElement = activeMoveRef?.current
    const container = movesContainerRef?.current

    if (!activeElement || !container) return

    const containerRect = container.getBoundingClientRect()
    const activeRect = activeElement.getBoundingClientRect()

    const activeLeftPadding = activeRect.left - containerRect.left
    const activeRightPadding = containerRect.right - activeRect.right
    const activeTopPadding = activeRect.top - containerRect.top
    const activeBottomPadding = containerRect.bottom - activeRect.bottom

    container.scrollTo({
      top:
        activeBottomPadding < 40
          ? container.scrollTop + Math.abs(activeBottomPadding) + 40
          : activeTopPadding < 40
            ? container.scrollTop - (Math.abs(activeTopPadding) + 40)
            : undefined,
      left:
        activeRightPadding < 40
          ? container.scrollLeft + Math.abs(activeRightPadding) + 40
          : activeLeftPadding < 40
            ? container.scrollLeft - (Math.abs(activeLeftPadding) + 40)
            : undefined,
      behavior: "smooth",
    })
  }, [dependency])

  return (
    <ScrollContext.Provider value={{ activeMoveRef }}>
      {children(movesContainerRef)}
    </ScrollContext.Provider>
  )
}

export default ScrollProvider

export function useScrollContext() {
  const context = useContext(ScrollContext)
  if (context === undefined) {
    throw new Error("useScrollContext must be used within a ScrollProvider")
  }
  return context
}
